import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../supabase";

// ── Design tokens — matches Landing ─────────────
const C = {
    bg: "#ffffff",
    surface: "#f7f7f7",
    border: "#e0e0e0",
    text: "#000000",
    muted: "#333333",
    dim: "#777777",
    accent: "#52658F",
    cyan: "#2a6a8a",
    green: "#059669",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

const PLAN_META = {
    free: {
        label: "Free",
        color: C.dim,
        accentBorder: C.border,
        checksPerMonth: 3,
        aiPerMonth: 5,
        rowLimit: 500,
    },
    pro: {
        label: "Pro",
        color: C.accent,
        accentBorder: C.accent,
        checksPerMonth: Infinity,
        aiPerMonth: 1000,
        rowLimit: Infinity,
    },
    enterprise: {
        label: "Enterprise",
        color: C.cyan,
        accentBorder: C.cyan,
        checksPerMonth: Infinity,
        aiPerMonth: Infinity,
        rowLimit: Infinity,
    },
};

// ── Contact Modal ─────────────────────────────────
function ContactModal({ plan, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    async function handleSubmit() {
        if (!form.name || !form.email) { setError("Name and email are required."); return; }
        if (!form.email.includes("@")) { setError("Please enter a valid email."); return; }
        setLoading(true); setError("");
        try {
            const res = await fetch(process.env.REACT_APP_API_URL + "/contact", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, plan }),
            });
            if (!res.ok) throw new Error("Failed");
            setSent(true);
        } catch { setError("Something went wrong. Email hello@datagetsbalanced.com directly."); }
        setLoading(false);
    }

    const inputStyle = {
        background: "white", border: "1.5px solid " + C.border, borderRadius: 8,
        padding: "0.65rem 1rem", color: C.text, fontFamily: MONO, fontSize: "0.85rem",
        width: "100%", outline: "none", boxSizing: "border-box",
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }} onClick={onClose}>
            <div style={{ background: "white", border: "1px solid " + C.border, borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 480, position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
                <button onClick={onClose} style={{ position: "absolute", top: "1rem", right: "1rem", background: "transparent", border: "1px solid " + C.border, borderRadius: 6, width: 28, height: 28, color: C.dim, cursor: "pointer", fontSize: "0.9rem" }}>✕</button>
                {sent ? (
                    <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>✓</div>
                        <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1.2rem", color: C.text, marginBottom: "0.5rem" }}>Message sent!</div>
                        <div style={{ color: C.muted, fontSize: "0.85rem", fontFamily: MONO }}>We'll get back to you within 1 business day.</div>
                        <button onClick={onClose} style={{ marginTop: "1.5rem", background: C.accent, color: "white", border: "none", padding: "0.65rem 1.5rem", borderRadius: 8, fontFamily: MONO, fontWeight: 700, cursor: "pointer" }}>Done</button>
                    </div>
                ) : (
                    <>
                        <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>{plan.toUpperCase()} INQUIRY</div>
                        <h2 style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1.4rem", color: C.text, marginBottom: "0.4rem" }}>Get in touch</h2>
                        <p style={{ color: C.muted, fontSize: "0.85rem", marginBottom: "1.5rem", fontFamily: MONO }}>Tell us about your business and we'll find the right plan.</p>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                            {[
                                { label: "NAME *", key: "name", placeholder: "Your name", type: "text" },
                                { label: "EMAIL *", key: "email", placeholder: "you@company.com", type: "email" },
                                { label: "COMPANY", key: "company", placeholder: "Company name (optional)", type: "text" },
                            ].map(f => (
                                <div key={f.key}>
                                    <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.3rem", fontFamily: MONO, fontWeight: 600, letterSpacing: "0.06em" }}>{f.label}</div>
                                    <input style={inputStyle} placeholder={f.placeholder} type={f.type} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                                </div>
                            ))}
                            <div>
                                <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.3rem", fontFamily: MONO, fontWeight: 600, letterSpacing: "0.06em" }}>MESSAGE</div>
                                <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} placeholder="What does your reconciliation process look like today?" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                            </div>
                        </div>
                        {error && <div style={{ marginTop: "0.75rem", fontSize: "0.78rem", color: "#c0392b" }}>{error}</div>}
                        <button onClick={handleSubmit} disabled={loading}
                            style={{ marginTop: "1.25rem", width: "100%", background: C.accent, color: "white", border: "none", padding: "0.8rem", borderRadius: 8, fontFamily: MONO, fontWeight: 700, fontSize: "0.9rem", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
                            {loading ? "Sending..." : "Send message →"}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, sub, color }) {
    return (
        <div style={{ background: "white", border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem 1.5rem", flex: 1, minWidth: 140, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: "0.65rem", color: C.dim, fontFamily: MONO, fontWeight: 600, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>{label.toUpperCase()}</div>
            <div style={{ fontSize: "1.8rem", fontWeight: 700, color: color || C.text, fontFamily: SYNE, letterSpacing: "-0.02em" }}>{value}</div>
            {sub && <div style={{ fontSize: "0.75rem", color: C.dim, marginTop: "0.25rem", fontFamily: MONO }}>{sub}</div>}
        </div>
    );
}

export default function AccountPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [profile, setProfile] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contactPlan, setContactPlan] = useState(null);

    useEffect(() => {
        if (!document.querySelector('link[href*="Fraunces"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet"; link.href = FONT_LINK;
            document.head.appendChild(link);
        }
    }, []);

    useEffect(() => {
        if (!user) return;
        async function load() {
            setLoading(true);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) { setLoading(false); return; }

            let { data: prof, error } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();

            if (error?.code === "PGRST116" || (!prof && !error)) {
                const { data: created } = await supabase
                    .from("profiles")
                    .insert({ id: session.user.id, plan: "free", ai_calls_this_month: 0, ai_calls_reset_at: new Date().toISOString() })
                    .select().single();
                prof = created;
            }
            setProfile(prof);

            const { data: rep } = await supabase.from("reports").select("*").eq("user_id", session.user.id).order("created_at", { ascending: false }).limit(20);
            setReports(rep || []);
            setLoading(false);
        }
        load();
    }, [user]);

    async function handleLogout() {
        await supabase.auth.signOut();
        navigate("/");
    }

    const plan = profile?.plan || "free";
    const meta = PLAN_META[plan] || PLAN_META.free;
    const aiUsed = profile?.ai_calls_this_month || 0;
    const now = new Date();
    const checksThisMonth = reports.filter(r => {
        const d = new Date(r.created_at);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const aiLimit = (meta.aiPerMonth === Infinity || plan === "pro") ? "Unlimited" : meta.aiPerMonth;
    const checkLimit = meta.checksPerMonth === Infinity ? "Unlimited" : meta.checksPerMonth;

    return (
        <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: MONO, overflowX: "hidden" }}>
            {contactPlan && <ContactModal plan={contactPlan} onClose={() => setContactPlan(null)} />}

            {/* Navbar — black, matches landing */}
            <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 2rem", background: "#000000", position: "sticky", top: 0, zIndex: 10 }}>
                <img src={require("../assets/dgb-logo-dark.png")} alt="DGB" style={{ height: 72, cursor: "pointer", marginLeft: "-8px" }} onClick={() => navigate("/")} />
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    <button onClick={() => navigate("/app")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: MONO, fontSize: "0.85rem", cursor: "pointer" }}>← Back to app</button>
                    <button onClick={handleLogout} style={{ background: "transparent", border: "1px solid #333", borderRadius: 6, padding: "0.35rem 1rem", color: "#888", fontFamily: MONO, fontSize: "0.75rem", cursor: "pointer" }}>Log out</button>
                </div>
            </nav>

            <div style={{ maxWidth: 860, margin: "0 auto", padding: "3rem 2rem" }}>

                {/* Header */}
                <div style={{ marginBottom: "2.5rem" }}>
                    <h1 style={{ fontFamily: SYNE, fontSize: "2.2rem", fontWeight: 800, color: C.text, marginBottom: "0.4rem", letterSpacing: "-0.02em" }}>Your Account</h1>
                    <div style={{ fontSize: "0.9rem", color: C.dim, fontFamily: MONO }}>{user?.email}</div>
                </div>

                {loading ? (
                    <div style={{ color: C.dim, fontSize: "0.85rem", fontFamily: MONO }}>Loading...</div>
                ) : (
                    <>
                        {/* Plan card */}
                        <div style={{ background: plan === "pro" ? "#eef1f7" : "white", border: "1.5px solid " + meta.accentBorder, borderRadius: 14, padding: "1.75rem 2rem", marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", boxShadow: plan !== "free" ? "0 4px 20px rgba(82,101,143,0.1)" : "none" }}>
                            <div>
                                <div style={{ fontSize: "0.65rem", color: C.dim, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>CURRENT PLAN</div>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.4rem" }}>
                                    <span style={{ fontFamily: SYNE, fontSize: "1.8rem", fontWeight: 800, color: meta.color, letterSpacing: "-0.02em" }}>{meta.label}</span>
                                    {plan === "free" && (
                                        <span style={{ fontSize: "0.72rem", color: C.dim, padding: "0.2rem 0.6rem", border: "1px solid " + C.border, borderRadius: 6, fontFamily: MONO }}>Free forever</span>
                                    )}
                                </div>
                                {plan === "free" && (
                                    <div style={{ fontSize: "0.82rem", color: C.muted, fontFamily: MONO }}>
                                        Need more?{" "}
                                        <span style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }} onClick={() => navigate("/pricing")}>See Pro & Enterprise →</span>
                                    </div>
                                )}
                            </div>
                            {plan === "free" ? (
                                <button onClick={() => setContactPlan("pro")}
                                    style={{ background: C.accent, color: "white", border: "none", padding: "0.75rem 1.75rem", borderRadius: 8, fontFamily: MONO, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" }}>
                                    Upgrade to Pro →
                                </button>
                            ) : (
                                <button onClick={() => setContactPlan(plan)}
                                    style={{ background: "transparent", color: C.muted, border: "1.5px solid " + C.border, padding: "0.75rem 1.5rem", borderRadius: 8, fontFamily: MONO, fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                                    Contact us
                                </button>
                            )}
                        </div>

                        {/* Usage stats */}
                        <div style={{ marginBottom: "2rem" }}>
                            <div style={{ fontSize: "0.7rem", color: C.dim, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.75rem" }}>THIS MONTH</div>
                            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                                <StatCard
                                    label="Checks run"
                                    value={checksThisMonth}
                                    sub={`of ${checkLimit}`}
                                    color={plan === "free" && checksThisMonth >= meta.checksPerMonth ? "#c0392b" : C.accent}
                                />
                                <StatCard
                                    label="AI explanations"
                                    value={aiUsed}
                                    sub={`of ${aiLimit}`}
                                    color={plan === "free" && aiUsed >= meta.aiPerMonth ? "#c0392b" : C.green}
                                />
                                <StatCard
                                    label="Total checks"
                                    value={reports.length}
                                    sub="all time"
                                    color={C.text}
                                />
                            </div>
                        </div>

                        {/* Limit warning */}
                        {plan === "free" && checksThisMonth >= meta.checksPerMonth && (
                            <div style={{ background: "#fde8e8", border: "1px solid #ef4444", borderRadius: 10, padding: "1rem 1.25rem", marginBottom: "1.5rem", fontSize: "0.85rem", color: "#c0392b", fontFamily: MONO }}>
                                ⚠ You've used all 3 free checks this month.{" "}
                                <span style={{ color: C.accent, cursor: "pointer", fontWeight: 600, textDecoration: "underline" }} onClick={() => setContactPlan("pro")}>Contact us to upgrade.</span>
                            </div>
                        )}

                        {/* Recent checks */}
                        {reports.length > 0 && (
                            <div>
                                <div style={{ fontSize: "0.7rem", color: C.dim, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.75rem" }}>RECENT CHECKS</div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                                    {reports.slice(0, 8).map(r => (
                                        <div key={r.id} style={{ background: "white", border: "1px solid " + C.border, borderRadius: 10, padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                                            <div>
                                                <div style={{ fontSize: "0.9rem", color: C.text, fontWeight: 600, fontFamily: MONO }}>
                                                    {r.pos_name || "File 1"} vs {r.fin_name || "File 2"}
                                                </div>
                                                <div style={{ fontSize: "0.75rem", color: C.dim, marginTop: "0.2rem", fontFamily: MONO }}>
                                                    {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", gap: "1.5rem", textAlign: "right" }}>
                                                <div>
                                                    <div style={{ fontSize: "0.65rem", color: C.dim, fontFamily: MONO, fontWeight: 600, letterSpacing: "0.08em" }}>ISSUES</div>
                                                    <div style={{ fontSize: "1rem", color: r.issue_count > 0 ? "#b45309" : C.green, fontWeight: 700, fontFamily: SYNE }}>{r.issue_count}</div>
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: "0.65rem", color: C.dim, fontFamily: MONO, fontWeight: 600, letterSpacing: "0.08em" }}>MATCH</div>
                                                    <div style={{ fontSize: "1rem", color: C.accent, fontWeight: 700, fontFamily: SYNE }}>
                                                        {r.total_rows > 0 ? Math.round((r.match_count / r.total_rows) * 100) : 0}%
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Privacy note */}
                        <div style={{ marginTop: "3rem", padding: "1.25rem 1.5rem", background: C.surface, border: "1px solid " + C.border, borderRadius: 12 }}>
                            <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.08em", marginBottom: "0.4rem" }}>PRIVACY & DATA</div>
                            <p style={{ fontSize: "0.82rem", color: C.muted, lineHeight: 1.7, margin: 0, fontFamily: MONO }}>
                                Your files are processed in your browser and never stored on our servers. Only anonymized report summaries (file names, row counts, issue counts) are saved here.
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* Footer */}
            <footer style={{ background: "#000000", padding: "1.5rem 2rem", textAlign: "center", marginTop: "2rem" }}>
                <span style={{ fontSize: "0.8rem", color: "#555", fontFamily: MONO }}>
                    © 2026 DGB ·{" "}
                    <a href="mailto:hello@datagetsbalanced.com" style={{ color: "#555", textDecoration: "none" }}>hello@datagetsbalanced.com</a>
                    {" · "}Jakarta, Indonesia
                </span>
            </footer>
        </div>
    );
}
